// src/components/calendar/EventCard.jsx
import parseStringDate from "../../utils/parseStringDate";
import { getEventTypeStyle as canonicalStyle } from "../../utils/eventTypeStyles";

/**
 * EventCard Component
 *
 * Displays individual event information in a card format.
 * Handles event styling based on type and provides click interaction.
 */
const EventCard = ({ event, showDate = false, onClick, driversById }) => {
  // ==========================================
  // STYLING UTILITIES
  // ==========================================

  /**
   * Get event type styling — delegates to the canonical palette in
   * `src/utils/eventTypeStyles.js`. The returned object has more keys
   * than the consumer below uses (bg, border, text, iconBg, icon); the
   * unused ones are harmless. See the utility file for the full set
   * and for why we keep the class names as literals (Tailwind JIT).
   */
  const getEventTypeStyle = (type) => {
    const s = canonicalStyle(type);
    return {
      bg: s.bg,
      border: s.border,
      text: s.text,
      icon: s.icon,
      iconBg: s.bgIcon,
    };
  };

  // ==========================================
  // FORMATTING UTILITIES
  // ==========================================

  /**
   * Format date for display
   */
  const formatDate = (dateStr) => {
    const date = parseStringDate(dateStr);
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  /**
   * Format time for display
   */
  const formatTime = (dateStr) => {
    const date = parseStringDate(dateStr);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // ==========================================
  // RENDER
  // ==========================================

  const style = getEventTypeStyle(event.eventType);

  return (
    <div
      className={`p-4 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all duration-200 ${style.bg} ${style.border} hover:border-opacity-80`}
      onClick={onClick}
    >
      <div className="flex items-start space-x-3">
        {/* Event Type Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 ${style.iconBg} rounded-full flex items-center justify-center`}
        >
          <span className="text-lg">{style.icon}</span>
        </div>

        {/* Event Content */}
        <div className="flex-1 min-w-0">
          {/* Event Title */}
          <h3 className={`font-medium ${style.text} truncate`}>
            {event.title}
          </h3>

          {/* Event Time */}
          <div className="flex items-center mt-1 text-sm text-gray-600">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>

            {showDate ? (
              <span>
                {formatDate(event.start)} • {formatTime(event.start)}
                {event.end && event.end !== event.start && (
                  <span> - {formatTime(event.end)}</span>
                )}
              </span>
            ) : (
              <span>
                {formatTime(event.start)}
                {event.end && event.end !== event.start && (
                  <span> - {formatTime(event.end)}</span>
                )}
              </span>
            )}
          </div>

          {/* Event Description */}
          {event.description && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">
              {event.description}
            </p>
          )}

          {/* Badges row — Job link + driver assignment */}
          {(event.driverId || (event.jobId && event.customerId)) && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {event.jobId && event.customerId && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Linked to Job
                </span>
              )}
              {event.driverId && (
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    driversById?.[event.driverId]?.disabled
                      ? "bg-gray-200 text-gray-600"
                      : "bg-emerald-100 text-emerald-800"
                  }`}
                  title={
                    driversById?.[event.driverId]?.disabled
                      ? "Assigned to a disabled fitter"
                      : "Assigned fitter"
                  }
                >
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7a1 1 0 011-1h9a1 1 0 011 1v9H3V7zm11 3h4l3 3v3h-7v-6zM5 19a2 2 0 100-4 2 2 0 000 4zm12 0a2 2 0 100-4 2 2 0 000 4z"
                    />
                  </svg>
                  {driversById?.[event.driverId]?.name || "Fitter"}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Chevron Icon */}
        <div className="flex-shrink-0">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
